import type { ApolloLink, NormalizedCacheObject } from '@apollo/client'

import { ApolloClient, InMemoryCache } from '@apollo/client/core'
import {
  provideApolloClient,
  provideApolloClients,
} from '@vue/apollo-composable'
import { destr } from 'destr'
import { createAuthLinks } from '~~/apollo/auth'
import { createCmsLinks } from '~~/apollo/cms'
import { createDefaultLinks } from '~~/apollo/default'
import { inMemoryCacheOptions } from '~~/apollo/local/inMemoryCacheOptions'

type ClientDictParams = Record<
  KnownClients,
  {
    link: ApolloLink
    persist?: boolean
  }
>

type KnownClients = 'auth' | 'cms' | 'default'

// Factory function for creating request-isolated clients
function createApolloClients(nuxtApp: any) {
  const {
    backend: { uriClient, uriServer },
    cms: { token: cmsToken, uri: cmsUri },
    ssoAuthUri,
    subscription: { uri: subscriptionUri },
  } = useRuntimeConfig().public

  const isServer = import.meta.server
  const isClient = import.meta.client

  const activeSubscriptionUri = isServer ? undefined : subscriptionUri

  // Each request gets fresh link instances
  const clientParams: ClientDictParams = {
    auth: { link: createAuthLinks(nuxtApp, ssoAuthUri) },
    cms: { link: createCmsLinks(nuxtApp, cmsUri, cmsToken) },
    default: {
      link: createDefaultLinks(
        nuxtApp,
        isClient ? uriClient : uriServer,
        activeSubscriptionUri,
      ),
      persist: true,
    },
  }

  // Request-isolated caches
  const caches: Record<KnownClients, InMemoryCache> = {
    auth: new InMemoryCache({
      // No result caching on server to prevent shared state
      resultCaching: isClient,
    }),
    cms: new InMemoryCache({
      resultCaching: isClient,
    }),
    default: new InMemoryCache({
      ...inMemoryCacheOptions,
      resultCaching: isClient,
    }),
  }

  // Fresh clients for each request
  const clients: Record<
    KnownClients,
    ApolloClient<NormalizedCacheObject>
  > = {} as any

  for (const [_key, { link, persist }] of Object.entries(clientParams)) {
    const key: KnownClients = _key as KnownClients

    clients[key] = new ApolloClient<NormalizedCacheObject>({
      cache: caches[key],
      defaultOptions: {
        query: {
          fetchPolicy: isServer
            ? persist
              ? 'network-only'
              : 'no-cache'
            : 'cache-first',
        },
        watchQuery: {
          fetchPolicy: isServer ? 'no-cache' : 'cache-first',
        },
      },
      devtools: { enabled: import.meta.dev },
      link,
      ssrForceFetchDelay: isClient ? 100 : 0,
      ssrMode: isServer,
    })

    // Handle cache persistence for client hydration
    if (persist) {
      const cacheKey = `_apollo:${key}`

      if (isServer) {
        // Server: Extract cache after rendering
        nuxtApp.hook('app:rendered', () => {
          nuxtApp.payload.data[cacheKey] = caches[key].extract()
          caches[key].reset()
        })
      } else if (isClient && nuxtApp.payload.data[cacheKey]) {
        // Client: Restore from SSR payload
        try {
          caches[key].restore(
            destr(JSON.stringify(nuxtApp.payload.data[cacheKey])),
          )
          // Clear payload data after restoration to free memory
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete nuxtApp.payload.data[cacheKey]
        } catch (error) {
          console.error(`Failed to restore Apollo ${key} cache:`, error)
        }
      }
    }
  }

  return { caches, clients }
}

export default defineNuxtPlugin({
  name: 'apollo',
  setup: (nuxtApp) => {
    const isServer = import.meta.server
    const isClient = import.meta.client

    // Create fresh, request-isolated clients
    const { clients } = createApolloClients(nuxtApp)

    // Server-side: Ensure cleanup after request
    if (isServer) {
      nuxtApp.hook('app:rendered', () => {
        // Explicit cleanup of clients after SSR
        Object.values(clients).forEach((client) => {
          if (client.stop) {
            client.stop()
          }
          // Clear any remaining cache references
          client.clearStore().catch(() => {})
        })
      })
    }

    // Client-side: Cleanup on navigation (handled by Nuxt3 lifecycle)
    if (isClient) {
      nuxtApp.hook('page:transition:finish', () => {
        // Optional: Garbage collect caches on navigation
        Object.values(clients).forEach((client) => {
          if (client.cache && typeof client.cache.gc === 'function') {
            client.cache.gc()
          }
        })
      })
    }

    // Provide clients to the app
    provideApolloClient(clients.default)
    provideApolloClients(clients)

    return {
      provide: {
        apollo: {
          clients,
          defaultClient: clients.default,
        },
      },
    }
  },
})
